import React, { createContext, useCallback, useContext, useRef, useMemo } from 'react';

type DraftSyncAndPublishContextType = {
	getIsPublishing: () => boolean;
	setIsPublishing: (value: boolean) => void;
	isPublishing: boolean;
};
const DraftSyncAndPublishContext = createContext<DraftSyncAndPublishContextType>({
	getIsPublishing: () => false,
	setIsPublishing: (_: boolean) => {
		// Intentional no-op
	},
	isPublishing: false,
});

export const DraftSyncAndPublishContextProvider = ({ children }) => {
	// These should remain in sync. The ref is used while a draft sync occurs, while
	// the state variable is used for re-rendering.
	const isPublishingRef = useRef<boolean>(false);
	const [isPublishing, setIsPublishing] = React.useState(false);

	const getIsPublishing = useCallback(() => isPublishingRef.current, []);

	const setPublishing = useCallback((value: boolean) => {
		isPublishingRef.current = value;
		setIsPublishing(value);
	}, []);

	const contextValue = useMemo(
		() => ({
			getIsPublishing,
			setIsPublishing: setPublishing,
			isPublishing,
		}),
		[getIsPublishing, setPublishing, isPublishing],
	);

	return (
		<DraftSyncAndPublishContext.Provider value={contextValue}>
			{children}
		</DraftSyncAndPublishContext.Provider>
	);
};

export const DraftSyncAndPublishContextConsumer = ({ children }) => {
	const context = useDraftSyncAndPublishContext();
	return children(context);
};

export const useDraftSyncAndPublishContext = () => {
	return useContext(DraftSyncAndPublishContext);
};
